import { useEffect, useMemo, useState } from "react";
import type { ChannelSessionDescriptor, PageContext } from "common";
import {
  useClaudeChannel,
  type ClaudeChannelSendResult,
} from "@via/hooks/useClaudeChannel";
import { useDeleteClaudeChannelConversation } from "@via/api/claude-channel.api";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { SessionList } from "./session-list";
import { ConversationView } from "./conversation-view";
import { ApprovalCard } from "./approval-card";
import { ApprovalHistory } from "./approval-history";

type View =
  | { kind: "list" }
  | { kind: "history" }
  | { kind: "draft"; sessionId: string; conversationId?: number }
  | { kind: "conversation"; conversationId: number };

const TITLE_LENGTH = 80;

/** What the drawer says under the input when a message was not accepted. The text stays in the input. */
const NOT_SENT_COPY: Record<
  Extract<ClaudeChannelSendResult, { ok: false }>["reason"],
  string
> = {
  auth_expired:
    "Not sent. Your Foundry Connect sign in has expired or the feature was turned off. Sign in again, then send it again.",
  session_gone: "Not sent. That Claude Code session is no longer connected.",
  not_found: "Not sent. This conversation no longer exists.",
  disconnected:
    "Not sent. The drawer is not connected to Foundry Connect right now. Send it again once it reconnects.",
  no_answer:
    "Not confirmed. Foundry Connect did not answer in time, so the message may not have reached Claude Code.",
};

const labelOf = (
  s: Pick<ChannelSessionDescriptor, "machine" | "folder"> | undefined
) => (s ? `${s.machine}: ${s.folder}` : "Ended session");

/** The Claude Code mode of the AI drawer (agent-6-2). Never needs an AI integration credential. */
export const ClaudeCodePanel = () => {
  const channel = useClaudeChannel();
  const deleteConversation = useDeleteClaudeChannelConversation();
  const [view, setView] = useState<View>({ kind: "list" });
  const [notice, setNotice] = useState<string | null>(null);
  const { loadMessages } = channel;

  const liveById = useMemo(
    () => new Map(channel.sessions.map(s => [s.sessionId, s])),
    [channel.sessions]
  );
  const openConversationId =
    view.kind === "conversation" ? view.conversationId : null;
  const openConversation = channel.conversations.find(
    c => c.id === openConversationId
  );

  useEffect(() => {
    if (openConversationId !== null) {
      loadMessages(openConversationId).catch(() =>
        setNotice("The messages of this conversation could not be loaded.")
      );
    }
  }, [openConversationId, loadMessages]);

  const send = async (
    conversationId: number,
    text: string,
    context?: PageContext
  ): Promise<string | null> => {
    const result = await channel.send(conversationId, text, context);
    return result.ok ? null : NOT_SENT_COPY[result.reason];
  };

  const sendFromDraft = async (
    draft: Extract<View, { kind: "draft" }>,
    text: string,
    context?: PageContext
  ): Promise<string | null> => {
    // A retry after a refused first message reuses the conversation that message opened.
    let conversationId = draft.conversationId;
    if (conversationId === undefined) {
      if (!channel.connected) return NOT_SENT_COPY.disconnected;
      const conversation = await channel.openConversation(
        draft.sessionId,
        text.slice(0, TITLE_LENGTH)
      );
      if (!conversation) {
        setNotice("That Claude Code session is no longer connected.");
        setView({ kind: "list" });
        return null;
      }
      conversationId = conversation.id;
      setView({ ...draft, conversationId });
    }
    const refusal = await send(conversationId, text, context);
    if (refusal === null) setView({ kind: "conversation", conversationId });
    return refusal;
  };

  const handleDelete = (conversationId: number) => {
    deleteConversation.mutate(conversationId, {
      onError: () => setNotice("The conversation could not be deleted."),
    });
    if (openConversationId === conversationId) setView({ kind: "list" });
  };

  const pendingCards = Object.entries(channel.cards).flatMap(
    ([sessionId, list]) => list.map(card => ({ sessionId, card }))
  );

  let body;
  if (view.kind === "history") {
    body = <ApprovalHistory onBack={() => setView({ kind: "list" })} />;
  } else if (view.kind === "draft") {
    const session = liveById.get(view.sessionId);
    body = (
      <ConversationView
        key={`draft:${view.sessionId}`}
        title="New conversation"
        sessionLabel={labelOf(session)}
        messages={[]}
        sessionLive={!!session}
        onBack={() => setView({ kind: "list" })}
        onSend={(text, context) => sendFromDraft(view, text, context)}
      />
    );
  } else if (view.kind === "conversation") {
    const session = openConversation
      ? liveById.get(openConversation.sessionId)
      : undefined;
    body = (
      <ConversationView
        key={`conversation:${view.conversationId}`}
        title={openConversation?.title || "Untitled conversation"}
        sessionLabel={labelOf(session ?? openConversation)}
        messages={channel.messages[view.conversationId] ?? []}
        sessionLive={!!session}
        onBack={() => setView({ kind: "list" })}
        onSend={(text, context) => send(view.conversationId, text, context)}
      />
    );
  } else {
    body = (
      <div className="flex-1 overflow-y-auto p-4">
        <SessionList
          sessions={channel.sessions}
          conversations={channel.conversations}
          onNewConversation={sessionId => setView({ kind: "draft", sessionId })}
          onSelectConversation={conversationId =>
            setView({ kind: "conversation", conversationId })
          }
          onDeleteConversation={handleDelete}
        />
        <button
          type="button"
          onClick={() => setView({ kind: "history" })}
          className="mt-4 text-xs font-medium text-indigo-700 underline hover:text-indigo-900">
          Approval history
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="claude-code-panel"
      className="flex h-full min-h-0 flex-col">
      {(channel.ended || notice) && (
        <div className="space-y-2 px-4 pt-3">
          {channel.ended && (
            <div>
              <MessageBanner
                messages={[
                  "The connection to your Claude Code sessions ended. Your sign in may have expired or the feature was turned off.",
                ]}
                type={BannerType.Warning}
              />
              <button
                type="button"
                onClick={channel.reconnect}
                className="mt-1 text-xs font-medium text-indigo-700 underline">
                Reconnect
              </button>
            </div>
          )}
          {notice && (
            <div>
              <MessageBanner messages={[notice]} type={BannerType.Warning} />
              <button
                type="button"
                onClick={() => setNotice(null)}
                className="mt-1 text-xs font-medium text-gray-600 underline">
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
      {pendingCards.length > 0 && (
        <section
          aria-label="Approval requests"
          className="space-y-2 border-b border-gray-200 px-4 py-3">
          {pendingCards.map(({ sessionId, card }) => (
            <ApprovalCard
              key={`${sessionId}:${card.requestId}`}
              card={card}
              sessionLabel={labelOf(liveById.get(sessionId))}
              onVerdict={behavior =>
                channel.verdict(sessionId, card.requestId, behavior)
              }
              onDismiss={() => channel.dismiss(sessionId, card.requestId)}
              onClear={() => channel.clearClosedCard(sessionId, card.requestId)}
            />
          ))}
        </section>
      )}
      {body}
    </div>
  );
};
